import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Eye, Trash2, Paperclip, Link2, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Task, TaskStatus } from "../../types/tasks";
import { TASK_TYPE_ICON, TASK_STATUS_COLORS, TASK_STATUSES } from "../../constants/taskConstants";
import { TaskStatusBadge } from "./TaskStatusBadge";
import ActionMenu, { ActionMenuItem } from "../UI/ActionMenu";

interface Props {
  tasks: Task[];
  loading: boolean;
  onEdit: (task: Task) => void;
  onDelete: (id: string) => void;
  onStatusChange: (taskId: string, status: TaskStatus) => void;
  isAdmin: boolean;
  currentUserId: string;
}

const CHANGEABLE_STATUSES: TaskStatus[] = TASK_STATUSES.filter((s) => s !== "overdue");

const formatDue = (
  task: Task,
  t: (k: string) => string,
): {
  date: string;
  time: string | null;
  tone: "default" | "today" | "overdue";
} => {
  const upcoming = task.activities
    .filter((a) => a.dueDate && !a.completedAt)
    .sort((a, b) => {
      const aMs = new Date(`${a.dueDate}T${a.dueTime ?? "00:00"}`).getTime();
      const bMs = new Date(`${b.dueDate}T${b.dueTime ?? "00:00"}`).getTime();
      return aMs - bMs;
    })[0];
  if (!upcoming?.dueDate)
    return { date: t("list.noDueDate"), time: null, tone: "default" };
  const due = new Date(upcoming.dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDay = new Date(due);
  dueDay.setHours(0, 0, 0, 0);
  const timeLabel = upcoming.dueTime ?? null;
  if (dueDay.getTime() < today.getTime())
    return { date: t("list.overdue"), time: timeLabel, tone: "overdue" };
  if (dueDay.getTime() === today.getTime())
    return { date: t("list.today"), time: timeLabel, tone: "today" };
  return {
    date: due.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    time: timeLabel,
    tone: "default",
  };
};

const fmtCreated = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

interface StatusDropdownProps {
  task: Task;
  onStatusChange: (taskId: string, status: TaskStatus) => void;
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({ task, onStatusChange }) => {
  const { t } = useTranslation("tasks");
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setMenuStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      zIndex: 9999,
    });
  }, [open]);

  if (task.status === "done") {
    return <TaskStatusBadge status={task.status} />;
  }

  return (
    <div className="inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${TASK_STATUS_COLORS[task.status]} hover:opacity-80 transition-opacity`}
      >
        {t(`form.statusOptions.${task.status}`)}
        <ChevronDown className="w-3 h-3" />
      </button>

      {open && ReactDOM.createPortal(
        <div
          ref={menuRef}
          style={menuStyle}
          className="bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[130px]"
        >
          {CHANGEABLE_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onStatusChange(task._id, s);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2 ${s === task.status ? "font-semibold" : ""}`}
            >
              <span className={`inline-block w-2 h-2 rounded-full ${TASK_STATUS_COLORS[s].replace(/text-\S+/g, "").replace(/border\S*/g, "").trim()}`} />
              {t(`form.statusOptions.${s}`)}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
};

export const TaskTable: React.FC<Props> = ({
  tasks,
  loading,
  onEdit,
  onDelete,
  onStatusChange,
  isAdmin,
  currentUserId,
}) => {
  const { t } = useTranslation("tasks");
  const { t: tCommon } = useTranslation("common");

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-12 bg-gray-100 border-b border-gray-200" />
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-16 bg-gray-50 border-b border-gray-100" />
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto max-h-[50svh] md:max-h-[55vh] lg:max-h-[60vh] xl:max-h-[70vh] overflow-y-scroll border-b border-b-gray-300">
      <table className="min-w-full border-collapse border border-gray-300">
        <thead className="bg-gray-50 text-nowrap sticky top-0 z-1">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200 w-16">
              {t("list.columns.type")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
              {t("list.columns.description")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
              {t("list.columns.client")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
              {t("list.columns.status")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
              {t("list.columns.assignee")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
              {t("list.columns.due")}
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
              {tCommon("labels.actions")}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white">
          {tasks.map((task) => {
            const TypeIcon = TASK_TYPE_ICON[task.type];
            const due = formatDue(task, t);
            const dueTone =
              due.tone === "overdue"
                ? "text-red-600 font-medium"
                : due.tone === "today"
                  ? "text-blue-600 font-medium"
                  : "text-gray-500";

            const canDelete =
              isAdmin || task.createdById === currentUserId;

            return (
              <tr
                key={task._id}
                onClick={() => onEdit(task)}
                className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                  <TypeIcon className="w-5 h-5 text-gray-500" />
                </td>
                <td className="px-6 py-4 text-sm font-medium text-dark-900 border border-gray-200 max-w-md">
                  <div className="flex items-center gap-2">
                    <span className="truncate">{task.description}</span>
                    {task.useStructuredForm && (
                      <Paperclip
                        className="w-3.5 h-3.5 text-gray-400 shrink-0"
                        aria-hidden
                      />
                    )}
                    {task.linkedShipmentId && (
                      <span
                        title={task.linkedShipmentNumber ?? "Linked shipment"}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-600 border border-indigo-200 shrink-0"
                      >
                        <Link2 className="w-2.5 h-2.5" />
                        {task.linkedShipmentNumber ?? "Shipment"}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {task.taskNumber} · {fmtCreated(task.createdAt)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                  {task.clientName ?? "—"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm border border-gray-200">
                  <StatusDropdown task={task} onStatusChange={onStatusChange} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                  {task.assigneeName}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm border border-gray-200 ${dueTone}`}
                >
                  <div>{due.date}</div>
                  {due.time && (
                    <div className="text-xs font-normal text-gray-400">
                      {due.time}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium border border-gray-200">
                  <div
                    className="flex items-center justify-end"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ActionMenu
                      ariaLabel={tCommon("labels.actions")}
                      items={
                        [
                          {
                            label: tCommon("buttons.view"),
                            icon: <Eye className="w-4 h-4" />,
                            onClick: () => onEdit(task),
                          },
                          canDelete && {
                            label: tCommon("buttons.delete"),
                            icon: <Trash2 className="w-4 h-4" />,
                            onClick: () => onDelete(task._id),
                            variant: "danger",
                            divider: true,
                          },
                        ].filter(Boolean) as ActionMenuItem[]
                      }
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {tasks.length === 0 && !loading && (
        <div className="text-center py-12">
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t("list.empty")}
          </h3>
        </div>
      )}
    </div>
  );
};
